﻿import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  UserPlus, Pencil, Trash2, X, BarChart3,
  Crown, Award, DollarSign, Trophy, Volleyball,
  Star, User as UserIcon, Shield, Check, Users as UsersIcon,
  Camera, Search, Mail, Phone as PhoneIcon, Gavel
} from "lucide-react";
import { Panel } from "../components/primitives";
import { useToast } from "../components/Toast";
import HeroBanner from "../components/HeroBanner";
import { useConfirm } from "../components/ConfirmDialog";
import { useActiveTeam } from "../components/ActiveTeam";
import { useVersion } from "../components/Version";
import PlayerProfile from "../components/PlayerProfile";
import MembersMobile from "./MembersMobile";
import MobileFormPage from "../components/MobileFormPage";
import { api } from "../api";
import { canDo, SKILL_BY_CODE, skillsForSport } from "../data";
import Avatar from "../components/Avatar";
import MemberCard from "../components/MemberCard";
import { compressAvatar } from "../utils/avatar";

// Roles selectable when adding / editing a member.
// Intentionally omits CRICKET_HEAD, VOLLEYBALL_HEAD and CAPTAIN — those
// are club/team-level roles assigned elsewhere (team.captainId in the
// Teams page for captains; committee membership in the League workspace
// for the two Head roles). Keeping them out of this picker prevents
// duplicate/parallel assignment of the same concept in two places.
// MEMBER was removed everywhere in favor of PLAYER as the default catch-all.
const ALL_ROLES = [
  "PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD",
  "PLAYER","UMPIRE","ADMIN"
];

// Display metadata per role — label, icon, and a distinctive accent colour used
// by the fancy role-picker chips.
const ROLE_META = {
  PRESIDENT:       { label: "President",       Icon: Crown,       color: "#f7c948" },
  VICE_PRESIDENT:  { label: "Vice President",  Icon: Award,       color: "#b07cff" },
  FINANCE_HEAD:    { label: "Finance Head",    Icon: DollarSign,  color: "#43d893" },
  CRICKET_HEAD:    { label: "Cricket Head",    Icon: Trophy,      color: "#ff8a2a" },
  VOLLEYBALL_HEAD: { label: "Volleyball Head", Icon: Volleyball,  color: "#3ed3e0" },
  CAPTAIN:         { label: "Captain",         Icon: Star,        color: "#ffd24a" },
  PLAYER:          { label: "Player",          Icon: UserIcon,    color: "#5ea8ff" },
  UMPIRE:          { label: "Umpire",          Icon: Gavel,       color: "#ff8a2a" },
  ADMIN:           { label: "Admin",           Icon: Shield,      color: "#ff6b6b" }
};
function roleLabel(role) { return ROLE_META[role]?.label || role.replace("_", " "); }

// Strip any legacy MEMBER assignment from a role list; if that leaves nothing,
// fall back to PLAYER so the user always has a meaningful role.
function normalizeRoles(roles) {
  const filtered = (roles || []).filter(r => r !== "MEMBER");
  return filtered.length ? filtered : ["PLAYER"];
}

const EMPTY_FORM = {
  firstName: "", lastName: "", email: "", phone: "",
  password: "gabruz123", roles: ["PLAYER"], teamIds: [],
  // { [teamId]: [skillCode, ...] } — populated by the per-team skill
  // picker that shows up once a team is selected. The chip catalog is
  // driven by that team's sport (cricket vs volleyball).
  skillsByTeam: {}
};

export default function Members({ state, reload, user }) {
  const { version } = useVersion();
  const toast = useToast();
  const confirm = useConfirm();
  const { activeTeam, activeTeamId, isAllTeams } = useActiveTeam();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // Avatar upload (only visible when editing an existing member since
  // we need the user id to POST against).
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileRef = useRef(null);
  const editingMember = editingId ? state.members.find(m => m.id === editingId) : null;

  async function onAvatarPicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editingId) return;
    setUploadingAvatar(true);
    try {
      const dataUrl = await compressAvatar(file, { brand: true });
      await api.uploadAvatar(editingId, dataUrl);
      toast.success("Photo updated.");
      await reload();
    } catch (err) {
      toast.error(err.message || "Couldn't upload photo.");
    } finally { setUploadingAvatar(false); }
  }
  async function onAvatarRemove() {
    if (!editingId) return;
    if (!(await confirm("Remove this member's photo?"))) return;
    setUploadingAvatar(true);
    try {
      await api.deleteAvatar(editingId);
      toast.success("Photo removed.");
      await reload();
    } catch (err) {
      toast.error(err.message || "Couldn't remove photo.");
    } finally { setUploadingAvatar(false); }
  }

  // Esc closes the desktop modal. Listener only attaches while it's
  // open so it can't intercept Esc elsewhere on the page.
  useEffect(() => {
    if (!showForm) return;
    const onKey = e => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm]);
  // Phase 3 — cricket player profile modal. profileFor holds the player
  // + cricket team context, or null when closed.
  const [profileFor, setProfileFor] = useState(null);

  const canCreate = canDo(user, "members", "create");
  const canUpdate = canDo(user, "members", "update");
  const canDelete = canDo(user, "members", "delete");

  // Phase 3 — scope to active team unless leadership has picked "All Teams".
  // Search + filter state for the modern card grid.
  const [search, setSearch]   = useState("");
  const [filter, setFilter]   = useState("all");   // all | leadership | players | cricket | volleyball | inactive

  const sortedMembers = useMemo(() => {
    let pool = state.members;
    if (!isAllTeams) {
      const ids = new Set(activeTeam?.players || []);
      pool = pool.filter(m => ids.has(m.id));
    }
    return [...pool].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
    );
  }, [state.members, activeTeam, isAllTeams]);

  // Filter chip predicates. `leadership` = anyone with a role other
  // than PLAYER / MEMBER; `players` = the reverse; sport filters
  // check team membership. Inactive members aren't in the response
  // at all — admins triage them from Admin â†’ Inactive Members.
  const LEADERSHIP_ROLES = ["ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD","CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN"];
  function memberMatchesFilter(m) {
    if (filter === "all")        return true;
    const roles = m.roles || [];
    if (filter === "leadership") return roles.some(r => LEADERSHIP_ROLES.includes(r));
    if (filter === "players")    return !roles.some(r => LEADERSHIP_ROLES.includes(r));
    if (filter === "umpires")    return roles.includes("UMPIRE");
    const memberTeams = state.teams.filter(t => t.players?.includes(m.id));
    if (filter === "cricket")    return memberTeams.some(t => t.sport === "Cricket");
    if (filter === "volleyball") return memberTeams.some(t => t.sport === "Volleyball");
    return true;
  }
  function memberMatchesSearch(m) {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.name  || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q)
    );
  }
  const visibleMembers = useMemo(
    () => sortedMembers.filter(m => memberMatchesFilter(m) && memberMatchesSearch(m)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortedMembers, filter, search, state.teams]
  );

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(""); setOk("");
  }

  function toggleInList(field, value) {
    setForm(prev => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(value) ? list.filter(x => x !== value) : [...list, value]
      };
    });
  }

  function openCreate() {
    setEditingId(null);
    // Pre-select the active team so new members default onto whichever team
    // the page is currently scoped to.
    setForm({ ...EMPTY_FORM, teamIds: activeTeam ? [activeTeam.id] : [] });
    setShowForm(true);
    setError(""); setOk("");
  }

  function openEdit(m) {
    setEditingId(m.id);
    const memberTeams = state.teams.filter(t => t.players?.includes(m.id));
    // Seed skillsByTeam from each team's existing playerSkills entry so
    // the edit form comes up already reflecting the current tags.
    const skillsByTeam = Object.fromEntries(
      memberTeams.map(t => [t.id, [...(t.playerSkills?.[m.id] || [])]])
    );
    setForm({
      firstName: m.firstName || "",
      lastName:  m.lastName  || "",
      email:     m.email     || "",
      phone:     m.phone     || "",
      password:  "",   // not edited from this UI
      roles:     normalizeRoles(m.roles),
      teamIds:   memberTeams.map(t => t.id),
      skillsByTeam
    });
    setShowForm(true);
    setError(""); setOk("");
  }

  function close() {
    setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); setError(""); setOk("");
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setOk("");

    if (!form.email.trim() || !form.firstName.trim() || !form.phone.trim()) {
      setError("First name, email, and phone are required.");
      return;
    }
    if (form.roles.length === 0) {
      setError("Pick at least one role.");
      return;
    }

    setBusy(true);
    try {
      const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim();
      let userId;

      if (editingId) {
        // Update the existing member
        await api.updateMember(editingId, {
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = editingId;
      } else {
        const r = await api.createMember({
          email:     form.email.trim(),
          password:  form.password || "gabruz123",
          firstName: form.firstName.trim(),
          lastName:  form.lastName.trim() || null,
          name:      fullName,
          phone:     form.phone.trim(),
          roles:     form.roles
        });
        userId = r.id;
      }

      // Reconcile team membership — diff against current memberships.
      const currentMemberships = state.teams.filter(t => t.players?.includes(userId)).map(t => t.id);
      const toAdd    = form.teamIds.filter(tid => !currentMemberships.includes(tid));
      const toRemove = currentMemberships.filter(tid => !form.teamIds.includes(tid));
      const stayOn   = form.teamIds.filter(tid => currentMemberships.includes(tid));

      // Filter skills to sport-appropriate codes per team before send —
      // guards against a mid-flight team swap where the picker briefly
      // held codes for the previous sport.
      function skillsForMember(team) {
        const catalog = new Set(skillsForSport(team.sport).map(s => s.code));
        return (form.skillsByTeam[team.id] || []).filter(c => catalog.has(c));
      }

      for (const teamId of toAdd) {
        const team = state.teams.find(t => t.id === teamId);
        const players = [...(team?.players || []), userId];
        // The MERGE on the server inserts the TeamPlayer row first;
        // writePlayerSkills is called after players is applied, so
        // sending both in one call is safe.
        await api.updateTeam(teamId, {
          players,
          playerSkills: { [userId]: skillsForMember(team) }
        });
      }
      for (const teamId of toRemove) {
        const team = state.teams.find(t => t.id === teamId);
        const players = (team?.players || []).filter(id => id !== userId);
        await api.updateTeam(teamId, { players });
      }
      // Teams the member is staying on: push any skill edits without
      // touching the roster (`players` omitted keeps it unchanged).
      for (const teamId of stayOn) {
        const team = state.teams.find(t => t.id === teamId);
        if (!team) continue;
        const nextSkills = skillsForMember(team);
        const prevSkills = team.playerSkills?.[userId] || [];
        if (nextSkills.length === prevSkills.length &&
            nextSkills.every(c => prevSkills.includes(c))) continue;
        await api.updateTeam(teamId, {
          playerSkills: { [userId]: nextSkills }
        });
      }

      setOk(editingId ? `Updated ${fullName}.` : `Added ${fullName}.`);
      close();
      await reload();
    } catch (err) {
      setError(err.message || "Failed to save member.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(m) {
    if (!(await confirm(`Deactivate member "${m.name}"? This soft-deletes them — they can be reactivated from the database.`))) return;
    try {
      await api.deleteMember(m.id);
      await reload();
    } catch (err) {
      toast.error("Delete failed: " + err.message);
    }
  }

  // Shared form fields — used by both desktop inline Panel and V4 MobileFormPage.
  const renderFormFields = () => (
    <>
      {editingId && editingMember && (
        <div className="avatarUpload avatarUpload--inline">
          <Avatar member={editingMember} size={72}/>
          <div className="avatarUpload-body">
            <label>Profile photo</label>
            <div className="avatarUpload-actions">
              <input
                ref={avatarFileRef}
                type="file"
                accept="image/*"
                onChange={onAvatarPicked}
                style={{ display: "none" }}
              />
              <button
                type="button"
                className="dark"
                onClick={() => avatarFileRef.current?.click()}
                disabled={uploadingAvatar}
              >
                <Camera size={13}/> {uploadingAvatar ? "Uploading…" : (editingMember.avatarUpdatedAt ? "Change" : "Upload")}
              </button>
              {editingMember.avatarUpdatedAt && (
                <button
                  type="button"
                  className="dark"
                  onClick={onAvatarRemove}
                  disabled={uploadingAvatar}
                  style={{ color: "var(--red)", borderColor: "rgba(255,107,107,0.4)" }}
                >
                  <Trash2 size={13}/> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="two">
        <div>
          <label>First Name *</label>
          <input value={form.firstName} onChange={e => update("firstName", e.target.value)} placeholder="Ashish" />
        </div>
        <div>
          <label>Last Name</label>
          <input value={form.lastName} onChange={e => update("lastName", e.target.value)} placeholder="Bhat" />
        </div>
      </div>
      <div className="two">
        <div>
          <label>Email *</label>
          <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="name@gabruzuniverse.com" disabled={!!editingId} />
        </div>
        <div>
          <label>Phone *</label>
          <input value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="412-555-0000" />
        </div>
      </div>
      {!editingId && (
        <div>
          <label>Initial Password</label>
          <input value={form.password} onChange={e => update("password", e.target.value)} placeholder="gabruz123" />
          <small style={{ display: "block", color: "var(--muted)", marginTop: 4 }}>
            Defaults to <code>gabruz123</code> if left blank. User can change it after first login.
          </small>
        </div>
      )}
      <div>
        <label className="mfp-sectionLabel">
          <span>Roles *</span>
          <em>{form.roles.length} selected</em>
        </label>
        <div className="rolePicker">
          {ALL_ROLES.map(role => {
            const meta = ROLE_META[role];
            const Icon = meta.Icon;
            const on = form.roles.includes(role);
            return (
              <button
                key={role}
                type="button"
                className={`roleChip${on ? " roleChip--on" : ""}`}
                style={{
                  "--role-color": meta.color,
                  "--role-color-glow": `${meta.color}33`
                }}
                onClick={() => toggleInList("roles", role)}
              >
                <span className="roleChip-iconWrap"><Icon size={16}/></span>
                <span className="roleChip-name">{meta.label}</span>
                {on && <span className="roleChip-check"><Check size={13} strokeWidth={3}/></span>}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="mfp-sectionLabel">
          <span>Team Assignment</span>
          <em>{form.teamIds.length} selected</em>
        </label>
        <div className="teamPicker">
          {state.teams.length === 0 && (
            <div className="teamPicker-empty">
              <UsersIcon size={18}/> No teams exist yet.
            </div>
          )}
          {state.teams.map(t => {
            const on = form.teamIds.includes(t.id);
            const isCricket = t.sport === "Cricket";
            return (
              <button
                key={t.id}
                type="button"
                className={`teamCard teamCard--${isCricket ? "cricket" : "volleyball"}${on ? " teamCard--on" : ""}`}
                onClick={() => toggleInList("teamIds", t.id)}
              >
                <span className="teamCard-iconWrap">
                  {isCricket ? <Trophy size={18}/> : <Volleyball size={18}/>}
                </span>
                <span className="teamCard-body">
                  <span className="teamCard-name">{t.name}</span>
                  <span className="teamCard-meta">
                    {t.sport} · {t.players?.length || 0} player{(t.players?.length || 0) === 1 ? "" : "s"}
                  </span>
                </span>
                <span className="teamCard-check" aria-hidden={!on}>
                  {on ? <Check size={14} strokeWidth={3}/> : null}
                </span>
              </button>
            );
          })}
        </div>
        <small className="mfp-fieldHint">
          Pick the teams this member should belong to. Edits add/remove them on save.
        </small>
      </div>

      {/* Per-team sport-specific skill picker — appears once at least one
          team is selected. The chip catalog switches per team's sport
          (cricket for cricket teams, volleyball for volleyball teams). */}
      {form.teamIds.length > 0 && (
        <div>
          <label className="mfp-sectionLabel">
            <span>Playing Skills</span>
            <em>{form.teamIds.length} team{form.teamIds.length === 1 ? "" : "s"}</em>
          </label>
          <div className="memSkillsWrap">
            {form.teamIds.map(tid => {
              const team = state.teams.find(t => t.id === tid);
              if (!team) return null;
              const catalog = skillsForSport(team.sport);
              const assigned = (form.skillsByTeam[tid] || []).filter(c => SKILL_BY_CODE[c]);
              const isCricket = team.sport === "Cricket";
              return (
                <div key={tid} className={`memSkills memSkills--${isCricket ? "cricket" : "volleyball"}`}>
                  <div className="memSkills-head">
                    <span className={`memSkills-sport memSkills-sport--${isCricket ? "cricket" : "volleyball"}`}>
                      {isCricket ? <Trophy size={11}/> : <Volleyball size={11}/>} {team.sport}
                    </span>
                    <b>{team.name}</b>
                    <small>{assigned.length}/{catalog.length}</small>
                  </div>
                  <div className="memSkills-chips">
                    {catalog.map(s => {
                      const on = assigned.includes(s.code);
                      return (
                        <button
                          type="button"
                          key={s.code}
                          className={`skillChip skillChip--${s.category}${on ? " on" : ""}`}
                          onClick={() => {
                            setForm(f => {
                              const cur = new Set(f.skillsByTeam[tid] || []);
                              if (cur.has(s.code)) cur.delete(s.code);
                              else cur.add(s.code);
                              return { ...f, skillsByTeam: { ...f.skillsByTeam, [tid]: [...cur] } };
                            });
                          }}
                          title={s.label}
                        >{s.short}</button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <small className="mfp-fieldHint">
            Tag the specialities for each team the member plays on. Cricket teams see
            batting / bowling / keeping options; volleyball teams see setter / hitter /
            blocker / libero.
          </small>
        </div>
      )}
    </>
  );

  // Desktop form — portalled modal popup so it opens over the members
  // list instead of pushing it down. Scrim click + top-right X + Esc
  // key all close it. Wrapped in createPortal so it lives at the root
  // stacking context (avoids z-index fights with the sticky topbar).
  const formModal = showForm && (canCreate || canUpdate) && createPortal((
    <div className="modal" onClick={close}>
      <div className="modalInner mem-modal-inner"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className="close"
          onClick={close}
          aria-label="Close"
        ><X size={20}/></button>
        <Panel title={editingId ? "Edit Member" : "Add New Member"}>
          <form className="form" onSubmit={submit}>
            {renderFormFields()}
            {error && <div className="err">{error}</div>}
            {ok    && <div className="note">{ok}</div>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" className="dark" onClick={close} disabled={busy}>Cancel</button>
              <button type="submit" className="btn" disabled={busy}>
                {busy ? "Saving…" : (editingId
                  ? <>Save Changes</>
                  : <><UserPlus size={16}/> Add Member</>)}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    </div>
  ), document.body);

  // V4 — branch into the mobile card list. When the form is open,
  // it takes over the full screen via MobileFormPage instead of
  // crowding the top of the members list.
  if (version === "v4") {
    if (showForm && (canCreate || canUpdate)) {
      return (
        <MobileFormPage
          title={editingId ? "Edit Member" : "Add New Member"}
          onBack={close}
          footer={
            <>
              <button type="button" className="mfp-btn mfp-btn--ghost" onClick={close} disabled={busy}>Cancel</button>
              <button type="submit" form="membersMfpForm" className="mfp-btn mfp-btn--primary" disabled={busy}>
                {busy ? "Saving…" : (editingId ? "Save Changes" : "Add Member")}
              </button>
            </>
          }
        >
          <form id="membersMfpForm" className="form" onSubmit={submit}>
            {renderFormFields()}
            {error && <div className="err">{error}</div>}
            {ok    && <div className="note">{ok}</div>}
          </form>
        </MobileFormPage>
      );
    }
    return (
      <MembersMobile
        state={state}
        reload={reload}
        user={user}
        openCreate={openCreate}
        openEdit={openEdit}
      />
    );
  }

  return (
    <div className="stack">
      <HeroBanner
        sport="Volleyball"
        altFlavor
        eyebrow="Directory · Community · Leadership"
        title="Members"
        subtitle="Every player, every captain, every role — the whole club in one roster."
        rightSlot={canCreate && (
          <button className="btn" onClick={openCreate}>
            <UserPlus size={16}/> Add Member
          </button>
        )}
      />

      {formModal}

      <Panel title={
        <div className="memList-title">
          <UsersIcon size={18}/>
          <span>{isAllTeams ? "All Members" : `${activeTeam?.name || "Team"} Members`}</span>
          <em className="memList-count">{visibleMembers.length}</em>
        </div>
      }>
        {/* Search + filter chips */}
        <div className="memList-toolbar">
          <div className="memList-search">
            <Search size={16}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, phone…"
              type="search"
            />
            {search && (
              <button
                type="button"
                className="memList-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              ><X size={14}/></button>
            )}
          </div>
          <div className="memList-filters">
            {[
              { key: "all",         label: "All" },
              { key: "leadership",  label: "Leadership" },
              { key: "players",     label: "Players" },
              { key: "umpires",     label: "Umpires" },
              { key: "cricket",     label: "Cricket" },
              { key: "volleyball",  label: "Volleyball" }
            ].map(f => (
              <button
                key={f.key}
                type="button"
                className={`memList-filter${filter === f.key ? " memList-filter--on" : ""}`}
                onClick={() => setFilter(f.key)}
              >{f.label}</button>
            ))}
          </div>
        </div>

        {/* Empty state */}
        {visibleMembers.length === 0 && (
          <div className="memList-empty">
            <UsersIcon size={28}/>
            <p>No members match your search.</p>
          </div>
        )}

        {/* Member card grid */}
        <div className="memberGrid">
          {visibleMembers.map(u => {
              const teams = state.teams.filter(t => t.players?.includes(u.id));
              const cricketTeams = teams.filter(t => t.sport === "Cricket");
              const profileTeam = !isAllTeams && activeTeam?.sport === "Cricket"
                ? activeTeam
                : cricketTeams[0];
              const primaryTeam = !isAllTeams && activeTeam
                ? activeTeam
                : (cricketTeams[0] || teams[0] || null);
              return (
                <MemberCard
                  key={u.id}
                  member={u}
                  teams={teams}
                  primaryTeam={primaryTeam}
                  canUpdate={canUpdate}
                  canDelete={canDelete}
                  showStats={!!profileTeam}
                  onStats={profileTeam ? () => setProfileFor({ user: u, team: profileTeam }) : undefined}
                  onEdit={() => openEdit(u)}
                  onRemove={() => remove(u)}
                />
              );
            })}
        </div>
      </Panel>

      {profileFor && (
        <PlayerProfile
          user={profileFor.user}
          team={profileFor.team}
          currentUser={user}
          onClose={() => setProfileFor(null)}
        />
      )}
    </div>
  );
}
